/**
 * Сопровождение покупки: чтение, отметки, исход, просьба о подтверждении
 * (BE-260, BE-261).
 *
 * Три правила слоя:
 *
 * * **необратимые действия без оптимистичного UI** (CLAUDE.md §8). Отметка
 *   исхода завершает заказ, просьба о подтверждении уходит письмом автохаусу —
 *   рисовать результат до ответа сервера нельзя. Отметки чек-листа обратимы, и
 *   у них оптимистичное состояние допустимо, но и оно не нужно: ответ приходит
 *   одним запросом и содержит новое состояние целиком;
 * * **ключ идемпотентности живёт до успеха, а не до рендера**: двойное нажатие
 *   и повтор после обрыва сети обязаны попасть в одно и то же действие;
 * * **ответ мутации кладётся в кэш**: он и есть новое состояние
 *   `GET /handover`, посчитанное в той же транзакции.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';
import { randomUuid } from '@shared/lib/uuid';

import type { ChecklistMarkInput, Handover, PurchaseOutcome } from './types';

function base(searchId: string): string {
  return `/search-sessions/${encodeURIComponent(searchId)}/handover`;
}

/**
 * Сопровождение заказа (шаг 7, §39).
 *
 * `404 HANDOVER_NOT_FOUND` — заказ до шага 7 не дошёл. Это **состояние, а не
 * сбой**: экран показывает, что решение по предложению ещё не принято.
 */
export function useHandover(searchId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.handover(searchId ?? ''),
    queryFn: () => apiFetch<Handover>(base(searchId ?? '')),
    enabled: enabled && searchId !== null && searchId !== '',
    retry: false,
  });
}

/**
 * Приём результата мутации сопровождения.
 *
 * Ответ мутации — **это и есть новое состояние** `GET /handover`. Он кладётся в
 * кэш напрямую; перезапрашиваются лишь данные, которых в нём нет: заказ и его
 * список — стадия воронки меняется вместе с исходом, и трекер в шапке обязан
 * это увидеть. Переписка — потому что просьба о подтверждении добавляет в неё
 * письмо.
 */
function useApplyHandover(searchId: string) {
  const queryClient = useQueryClient();
  return useCallback(
    (state: Handover) => {
      queryClient.setQueryData(queryKeys.handover(searchId), state);
      void queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(searchId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.searchSessions() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.negotiations() });
    },
    [queryClient, searchId],
  );
}

/**
 * Отметки чек-листа (§39.1).
 *
 * Обратимое действие: `Idempotency-Key` не требуется и не отправляется — оно
 * ничего наружу не шлёт и снимается тем же нажатием. Требовать ключ на каждый
 * чекбокс значило бы объявить необратимым то, что таковым не является.
 */
export function useMarkChecklist(searchId: string) {
  const apply = useApplyHandover(searchId);

  return useMutation({
    mutationFn: (marks: ChecklistMarkInput[]) =>
      apiFetch<Handover>(base(searchId), {
        method: 'PATCH',
        body: { checklist: marks },
      }),
    onSuccess: apply,
  });
}

/**
 * Отметка исхода покупки (§39.3).
 *
 * **Без оптимистичного UI и в два шага** (CLAUDE.md §8): «купил» завершает
 * заказ и запускает расчёт вознаграждения, и `confirmed` — это второй шаг,
 * который backend требует отдельно. Неактивная кнопка здесь подсказка, а
 * правило живёт в сервисном слое.
 */
export function useRecordOutcome(searchId: string) {
  const apply = useApplyHandover(searchId);

  return useMutation({
    mutationFn: ({ value, confirmed }: { value: PurchaseOutcome; confirmed: boolean }) =>
      apiFetch<Handover>(base(searchId), {
        method: 'PATCH',
        idempotencyKey: randomUuid(),
        body: { outcome: { value, confirmed } },
      }),
    onSuccess: apply,
  });
}

/**
 * Просьба к автохаусу подтвердить обещанное письменно (§39.1).
 *
 * Уходит письмом, поэтому необратима и идёт с ключом идемпотентности. Повтор
 * backend отклоняет (`409`): второе письмо о том же — уже напоминание, у
 * которого свой лимит и свой ритм.
 */
export function useRequestConfirmation(searchId: string) {
  const apply = useApplyHandover(searchId);

  return useMutation({
    mutationFn: () =>
      apiFetch<Handover>(`${base(searchId)}/confirmation`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
      }),
    onSuccess: apply,
  });
}
